import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Images, Paperclip, BellOff, Ban, Flag, Info } from "lucide-react";

import { useI18n } from "@/i18n/I18nContext";
import { conversationWrite, chatErrorCode, chatErrorMessage } from "@/lib/conversationWrite";
import { normalizeBlocks } from "@/lib/v3People";
import { convTitle, convIntro, isDirect, chatHref, senderLabel, reportTarget, normalizeMessages, sendNeedsCheck, sendOutcome, FAIL_TEXT } from "@/lib/v3Messages";
import { useMemberAuth } from "../MemberAuthContext";
import { useCore } from "../useCore";
import { mutate } from "../actions";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, ErrorNote } from "../components/kit";
import { V3Title, V3Columns, V3Actions, StatusBanner, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { V3TextArea, V3_GHOST } from "../components/v3app";
import { WithChat, ChatAside, ChatHead, ChatMissing, readMessages, readConversation, messagesPath } from "../components/v3messages";

const NO_RESPONSE = { data: null, rejected: null, status: null };

function Bubble({ m, label, reportTo }) {
  return (
    <li data-testid={`chat-msg-${m.id}`} data-mine={m.mine ? "true" : "false"}
      className={`rounded-[10px] border px-[16px] pb-[11px] pt-[12px] ${m.mine ? "ml-[38px] border-[#F2CDDD] bg-v3-blush lg:ml-[82px]" : "mr-[38px] border-v3-line bg-white lg:mr-[82px]"}`}>
      <p className="flex items-start justify-between gap-3 text-[12.5px] font-bold leading-[17px] text-v3-muted">
        <span>{label}<span className="sr-only">{m.mine ? " (du)" : ""}:</span></span>
        {reportTo && <Link to={reportTo} data-testid={`chat-msg-report-${m.id}`} className="-my-[4px] inline-flex min-h-[24px] items-center gap-1 rounded-[4px] px-[4px] font-bold text-v3-muted hover:text-v3-magenta focus-visible:outline focus-visible:outline-2 focus-visible:outline-v3-magenta">
          <Flag className="h-[13px] w-[13px]" aria-hidden="true" />Melden<span className="sr-only">: Nachricht von {label}</span></Link>}
      </p>
      <p className="mt-[6px] whitespace-pre-wrap break-words text-[16px] leading-[23px] text-v3-ink lg:leading-[22px]">{m.body}</p>
    </li>
  );
}

function Tools({ c, canWrite }) {
  return (
    <nav aria-label="Weitere Möglichkeiten im Gespräch" className="mt-[18px] flex flex-wrap gap-2" data-testid="chat-tools">
      <Link to={chatHref(c, "/medien")} data-testid="chat-tool-media" className={V3_GHOST}><Images className="h-4 w-4" aria-hidden="true" />Geteilte Medien</Link>
      {canWrite && <Link to={chatHref(c, "/anhang")} data-testid="chat-tool-attach" className={V3_GHOST}><Paperclip className="h-4 w-4" aria-hidden="true" />Anhang hinzufügen</Link>}
      {c.type === "group" && <Link to={chatHref(c, "/stummschalten")} data-testid="chat-tool-mute" className={V3_GHOST}><BellOff className="h-4 w-4" aria-hidden="true" />Gruppenchat stummschalten</Link>}
      {isDirect(c) && <Link to={`/kontakte/blockieren/${encodeURIComponent(c.other.id)}`} data-testid="chat-tool-block" className={V3_GHOST}><Ban className="h-4 w-4" aria-hidden="true" />Person blockieren</Link>}
    </nav>
  );
}

// M119: the draft stays; an unclear result is first checked against the thread instead of being sent twice.
function SendFailed({ c, f, busy, onRetry, onCheck, onBack }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const tx = FAIL_TEXT[f.kind];
  const who = convTitle(c);
  return (
    <div data-m="M119" data-testid="chat-send-failed" data-kind={f.kind} ref={ref} tabIndex={-1} className="outline-none">
      <V3Title title="Nachricht konnte nicht gesendet werden" intro={f.kind === "unclear" ? "Ob deine Nachricht angekommen ist, ist noch nicht bestätigt." : "Deine Nachricht wurde noch nicht erfolgreich gesendet."} testid="chat-failed-title" />
      <V3Columns layout="detail" aside={<ChatAside text={who} />} main={<>
        <StatusBanner tone="sand" icon={Info} title={tx.banner} text={who} testid="chat-failed-banner" />
        <DetailList>
          <DetailRow label="Empfänger" value={who} testid="chat-failed-recipient" />
          <DetailRow label="Nachricht" value="Bleibt als Entwurf erhalten" testid="chat-failed-draft" />
          <DetailRow label="Status" value={tx.status} testid="chat-failed-status" />
          <DetailRow label="Nächster Schritt" value={tx.next} testid="chat-failed-next" />
        </DetailList>
        {f.detail && <div className="mt-[12px]"><ErrorNote testid="chat-failed-detail">{f.detail}</ErrorNote></div>}
        <V3Actions>
          {f.kind === "unclear"
            ? <button type="button" onClick={onCheck} disabled={busy} data-testid="chat-failed-check" className={V3_PRIMARY}>{busy ? "Wird geprüft …" : "Verlauf prüfen"}</button>
            : <button type="button" onClick={onRetry} disabled={busy} data-testid="chat-failed-retry" className={V3_PRIMARY}>{busy ? "Wird gesendet …" : "Erneut senden"}</button>}
          <button type="button" onClick={onBack} disabled={busy} data-testid="chat-failed-back" className={V3_SECONDARY}>Zurück</button>
        </V3Actions>
      </>} />
    </div>
  );
}

function History({ thread, list, c, ownName }) {
  if (thread.error) return <div className="mb-[17px]"><ConnectionError onRetry={thread.reload} /></div>;
  if (thread.data && thread.data.list === null) return <div className="mb-[17px]" data-testid="chat-history-format"><ConnectionError onRetry={thread.reload} /></div>;
  if (!list) return <Loading label="Verlauf wird geladen …" />;
  if (!list.length) return <p className="mb-[17px] text-[14px] text-v3-muted" data-testid="chat-history-empty">Noch keine Nachrichten in diesem Gespräch.</p>;
  return (
    <ol aria-label="Verlauf" className="space-y-[17px]" data-testid="chat-history">
      {list.map((m) => {
        const target = reportTarget(c, m);
        return <Bubble key={m.id} m={m} label={senderLabel(m, c, ownName)} reportTo={target ? chatHref(c, `/melden/${encodeURIComponent(m.id)}`) : ""} />;
      })}
    </ol>
  );
}

// M17: GET/POST /member/conversations/{id}/messages (Core 1.1); writing only with the shared write check.
function Thread({ conv }) {
  const { t } = useI18n();
  const { member } = useMemberAuth();
  const [c, setC] = useState(conv);
  const thread = useCore(messagesPath(conv), (d) => ({ list: normalizeMessages(d) }));
  const blocks = useCore(isDirect(conv) ? "/member/blocks" : null, normalizeBlocks);
  const [list, setList] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [failure, setFailure] = useState(null);
  const [gone, setGone] = useState(false);
  const [announce, setAnnounce] = useState("");
  const flight = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { setList(thread.data ? thread.data.list : null); }, [thread.data]);
  const historyReady = !thread.error && Array.isArray(thread.data?.list) && Array.isArray(list);

  const blocked = isDirect(c) && Array.isArray(blocks.data) && blocks.data.some((b) => b.id === String(c.other?.id));
  const blocksPending = isDirect(c) && !blocks.data && !blocks.error;
  const writeOf = (x) => conversationWrite(x, { blocked });

  const settle = (o, fresh) => {
    if (o.state === "sent") { setDraft(""); setFailure(null); setList((l) => o.list || [...(l || []), o.message]); setAnnounce("Nachricht gesendet."); return; }
    if (o.state === "gone") { setGone(true); return; }
    if (o.state === "forbidden") {
      setFailure(null);
      setError(chatErrorMessage(chatErrorCode({ response: { status: 403 } }, { ctx: "message", direct: isDirect(c) }), t));
      if (fresh?.ok) { if (fresh.conv) setC(fresh.conv); else setGone(true); }
      return;
    }
    setFailure(o);
  };

  const send = async () => {
    const body = draft.trim();
    if (flight.current || blocksPending || !historyReady) return;
    if (!body) { setError("Bitte schreibe zuerst eine Nachricht."); return; }
    const w = writeOf(c);
    if (!w.canWrite) { setError(w.hint); return; }
    flight.current = true; setBusy(true); setError(""); setAnnounce("");
    const before = (list || []).map((m) => m.id);
    const r = await mutate("post", messagesPath(c), { body }, "Die Nachricht wurde nicht gesendet");
    if (!alive.current) { flight.current = false; return; }
    const reread = sendNeedsCheck(r) ? await readMessages(c) : null;
    if (!alive.current) { flight.current = false; return; }
    const o = sendOutcome(r, reread, before, body);
    const fresh = o.state === "forbidden" ? await readConversation(c.id) : null;
    flight.current = false;
    if (!alive.current) return;
    setBusy(false);
    settle(o, fresh);
  };

  const recheck = async () => {
    if (flight.current || !failure) return;
    flight.current = true; setBusy(true);
    const reread = await readMessages(c);
    flight.current = false;
    if (!alive.current) return;
    setBusy(false);
    settle(sendOutcome(NO_RESPONSE, reread, failure.before, failure.body));
  };

  if (gone) return <ChatMissing />;
  if (thread.error === "notfound" || thread.error === "forbidden") return <ChatMissing />;
  if (failure) return <SendFailed c={c} f={failure} busy={busy} onRetry={send} onCheck={recheck} onBack={() => setFailure(null)} />;
  const w = writeOf(c);
  const latest = list && list.length ? list[list.length - 1].body : "";
  return (
    <div data-m="M17" data-testid="chat-view">
      <V3Title title="Nachrichten und Gesprächsdetails" intro={convIntro(c)} testid="chat-title" />
      <V3Columns layout="detail" aside={<ChatAside text={latest || "Noch keine Nachricht"} />} main={<>
        <ChatHead c={c} />
        <History thread={thread} list={list} c={c} ownName={member?.name} />
        {w.canWrite ? (
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-[26px]" data-testid="chat-form" noValidate>
            <V3TextArea id="chat-input" label="Nachricht schreiben" value={draft} onChange={(e) => { setDraft(e.target.value); setError(""); }} rows={4} placeholder="Noch nicht eingegeben" error={error} />
            <V3Actions className="mt-[9px]"><button type="submit" disabled={busy || blocksPending || !historyReady} data-testid="chat-send" className={V3_PRIMARY}>{busy ? "Wird gesendet …" : "Nachricht senden"}</button></V3Actions>
          </form>
        ) : (
          <div className="mt-[26px]" role="status" data-testid="chat-readonly" data-reason={w.reason}>
            <StatusBanner tone="notice" title="Nur lesen" text={w.hint} />
            {error && <ErrorNote testid="chat-send-error">{error}</ErrorNote>}
          </div>
        )}
        <Tools c={c} canWrite={w.canWrite} />
        <p role="status" className="sr-only" data-testid="chat-announce">{announce}</p>
      </>} />
    </div>
  );
}

export default function MessageThread() {
  return <WithChat>{(c) => <Thread conv={c} />}</WithChat>;
}
